import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { districtsApi, provincesApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function DistrictsTab() {
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    district_code: '',
    name_en: '',
    name_lao: '',
    name_th: '',
    province_id: '',
    status: 'active'
  });
  const { getMessage, isLao, isThai, getCommonLabel } = useTranslation();

  useEffect(() => {
    fetchDistricts();
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDistricts(districts);
    } else {
      const q = searchQuery.toLowerCase();
      const filtered = districts.filter(district =>
        district.name_en?.toLowerCase().includes(q) ||
        district.name_lao?.toLowerCase().includes(q) ||
        district.name_th?.toLowerCase().includes(q) ||
        district.name?.toLowerCase().includes(q) ||
        district.code?.toLowerCase().includes(q) ||
        district.district_code?.toLowerCase().includes(q) ||
        district.province_name?.toLowerCase().includes(q) ||
        district.province_name_en?.toLowerCase().includes(q) ||
        district.province_name_lao?.toLowerCase().includes(q) ||
        district.province_name_th?.toLowerCase().includes(q)
      );
      setFilteredDistricts(filtered);
    }
  }, [searchQuery, districts]);

  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const response = await districtsApi.getAll();
      setDistricts(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error('Failed to load districts');
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProvinces = async () => {
    try {
      const response = await provincesApi.getAll();
      setProvinces(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching provinces:', error);
      toast.error('Failed to load provinces');
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddDistrict = () => {
    setEditingDistrict(null);
    setFormData({
      code: '',
      district_code: '',
      name_en: '',
      name_lao: '',
      name_th: '',
      province_id: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditDistrict = (district) => {
    setEditingDistrict(district);
    setFormData({
      code: district.code || '',
      district_code: district.district_code || '',
      name_en: district.name_en || '',
      name_lao: district.name_lao || '',
      name_th: district.name_th || '',
      province_id: district.province_id || '',
      status: district.status || 'active'
    });
    setShowModal(true);
  };

  const handleDeleteDistrict = async (district) => {
    if (!window.confirm('Are you sure you want to delete this district?')) {
      return;
    }

    try {
      await districtsApi.delete(district.id);
      toast.success('District deleted successfully');
      fetchDistricts();
    } catch (error) {
      console.error('Error deleting district:', error);
      toast.error('Failed to delete district');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        province_id: formData.province_id ? parseInt(formData.province_id) : null
      };

      if (editingDistrict) {
        await districtsApi.update(editingDistrict.id, data);
        toast.success('District updated successfully');
      } else {
        await districtsApi.create(data);
        toast.success('District created successfully');
      }
      
      setShowModal(false);
      fetchDistricts();
    } catch (error) {
      console.error('Error saving district:', error);
      toast.error('Failed to save district');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDistrict(null);
  };

  const getProvinceName = (provinceId) => {
    const province = provinces.find(p => p.id === provinceId);
    if (!province) return 'Unknown Province';
    if (isLao()) return province.name_lao || province.name_en || province.name_th;
    if (isThai()) return province.name_th || province.name_en || province.name_lao;
    return province.name_en || province.name_lao || province.name_th;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            🏘️ {getMessage('settings.districts') || 'Districts'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {getMessage('settings.districts_desc') || 'Manage districts and administrative divisions'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchDistricts}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            🔄 {getMessage('common.refresh') || 'Refresh'}
          </button>
          <button
            onClick={handleAddDistrict}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            ➕ {getCommonLabel('add') || 'Add District'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={getMessage('common.search') || 'Search districts...'}
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
            🔍
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {filteredDistricts.length} district(s) found
        </div>
      </div>

      {/* Districts Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        ) : filteredDistricts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🏘️</div>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery ? 'No districts found matching your search' : 'No districts available'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  District Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (EN)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (LAO)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name (TH)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Province
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              {filteredDistricts.map((district) => (
                <tr key={district.id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                      {district.district_code || district.code || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {district.name_en || district.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {district.name_lao || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {district.name_th || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {isLao() 
                          ? (district.province_name_lao || district.province_name_en || district.province_name_th || getProvinceName(district.province_id)) 
                          : (isThai() 
                              ? (district.province_name_th || district.province_name_en || district.province_name_lao || getProvinceName(district.province_id)) 
                              : (district.province_name_en || district.province_name || getProvinceName(district.province_id))
                            )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      district.status === 'active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                    }`}>
                      {district.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEditDistrict(district)}
                      className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteDistrict(district)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingDistrict ? 'Edit District' : 'Add New District'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  General Code
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g., VTE-1, LPB-34"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  District Code *
                </label>
                <input
                  type="text"
                  name="district_code"
                  value={formData.district_code}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., VTE-1, PSL-10"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (English) *
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (Lao) *
                </label>
                <input
                  type="text"
                  name="name_lao"
                  value={formData.name_lao}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name (Thai)
                </label>
                <input
                  type="text"
                  name="name_th"
                  value={formData.name_th}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Province *
                </label>
                <select
                  name="province_id"
                  value={formData.province_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Province</option>
                  {provinces.map(province => (
                    <option key={province.id} value={province.id}>
                      {isLao() ? (province.name_lao || province.name_en) : (province.name_en || province.name_lao)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  {editingDistrict ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}